import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError, merchantAuth } from '@/api/client';
import { Logo } from '@/components/brand/logo';
import { dirFor } from '@/i18n';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const Route = createFileRoute('/m/login')({
  component: MerchantLogin,
});

function MerchantLogin() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [merchantCode, setMerchantCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  // MFA second-step state. When set, the form switches to "enter code".
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = dirFor(i18n.language);
  }, [i18n.language]);

  const login = useMutation({
    mutationFn: () => merchantAuth.login(merchantCode, username, password),
    onSuccess: (res) => {
      // Backend returns { requires_mfa: true, challenge_token } when
      // the user has TOTP enabled — defer cookie issuance until step 2.
      if (res.requires_mfa) {
        setChallengeToken(res.challenge_token);
        return;
      }
      navigate({ to: '/m/dashboard' });
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiError ? e.message : (t('auth.invalid_credentials') as string),
      ),
  });

  const verifyMfa = useMutation({
    mutationFn: () => merchantAuth.loginMfa(challengeToken!, mfaCode),
    onSuccess: () => navigate({ to: '/m/dashboard' }),
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : (t('auth.invalid_code') as string)),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (challengeToken) {
      verifyMfa.mutate();
    } else {
      login.mutate();
    }
  };

  return (
    <main className="auth-backdrop min-h-screen grid place-items-center p-4">
      <Card className="w-full max-w-sm shadow-elev">
        <CardHeader className="items-center text-center space-y-3 pb-4">
          <Logo size="xl" className="drop-shadow-sm" />
          <div className="space-y-1">
            <CardTitle className="text-xl">
              {challengeToken ? t('auth.mfa_title') : t('auth.sign_in_title')}
            </CardTitle>
            <CardDescription>
              {challengeToken ? t('auth.mfa_help') : t('merchants.title')}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={onSubmit}>
            {!challengeToken && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="merchant">{t('merchants.code')}</Label>
                  <Input
                    id="merchant"
                    autoComplete="organization"
                    autoFocus
                    required
                    value={merchantCode}
                    onChange={(e) => setMerchantCode(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="username">{t('auth.username')}</Label>
                  <Input
                    id="username"
                    autoComplete="username"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </>
            )}
            {challengeToken && (
              <div className="space-y-1.5">
                <Label htmlFor="code">{t('auth.mfa_code')}</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6,8}"
                  autoFocus
                  required
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                />
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={login.isPending || verifyMfa.isPending}
            >
              {challengeToken ? t('auth.mfa_verify') : t('auth.sign_in')}
            </Button>
            {!challengeToken && (
              <div className="text-center text-xs text-muted-foreground pt-1">
                <Link to="/m/forgot-password" className="underline hover:text-foreground">
                  {t('merchant.forgot_password.link', { defaultValue: 'Forgot password?' })}
                </Link>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
